import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, BellRing, Loader2 } from "lucide-react";

const statusOf = (expiry) => {
  if (!expiry) return { key: "none", label: "No expiry date", tone: "bg-slate-100 text-slate-600 border-slate-200" };
  let ed;
  try { ed = new Date(String(expiry).slice(0, 10)); } catch { return { key: "none", label: "No expiry date", tone: "bg-slate-100 text-slate-600 border-slate-200" }; }
  const days = Math.ceil((ed - new Date()) / 86400000);
  if (days < 0) return { key: "expired", label: `Expired ${-days}d ago`, tone: "bg-red-100 text-red-800 border-red-300" };
  if (days <= 30) return { key: "expiring", label: `Expires in ${days}d`, tone: "bg-amber-100 text-amber-800 border-amber-300" };
  return { key: "valid", label: `Valid · ${days}d left`, tone: "bg-emerald-100 text-emerald-800 border-emerald-300" };
};

export default function AdminCompliance({ suppliers, filter, onFilterChange }) {
  const [sending, setSending] = useState("");
  const sendReminder = async (r) => {
    const key = `${r.sid}-${r.doc_type}`;
    setSending(key);
    try {
      const { data } = await api.post(`/admin/suppliers/${r.sid}/send-expiry-reminder`, { doc_type: r.doc_type, expiry_date: r.expiry });
      toast.success(data.message || "Reminder sent");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(""); }
  };
  const rows = [];
  (suppliers || []).forEach((s) => (s.documents || []).forEach((d) => {
    rows.push({ sid: s.id, company: s.company_name, email: s.email, doc_type: d.doc_type, expiry: d.expiry_date, file_id: d.file_id, st: statusOf(d.expiry_date) });
  }));
  const counts = {
    all: rows.length,
    expiring: rows.filter((r) => r.st.key === "expiring").length,
    expired: rows.filter((r) => r.st.key === "expired").length,
  };
  const filtered = rows.filter((r) => (filter === "all" ? true : r.st.key === filter))
    .sort((a, b) => (a.expiry || "9999").localeCompare(b.expiry || "9999"));
  const chips = [
    { k: "all", label: `All documents (${counts.all})` },
    { k: "expiring", label: `Expiring ≤30 days (${counts.expiring})` },
    { k: "expired", label: `Expired (${counts.expired})` },
  ];

  return (
    <div className="space-y-4" data-testid="admin-compliance">
      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <button
            key={c.k}
            onClick={() => onFilterChange(c.k)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${filter === c.k ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
            data-testid={`compliance-filter-${c.k}`}
          >{c.label}</button>
        ))}
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="compliance-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-5 py-3">SMME</th><th className="px-5 py-3">Document</th><th className="px-5 py-3">Expiry date</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500" data-testid="compliance-empty">No documents match this filter.</td></tr>}
            {filtered.map((r, i) => (
              <tr key={`${r.sid}-${r.doc_type}`} className="hover:bg-slate-50" data-testid={`compliance-row-${i}`}>
                <td className="px-5 py-3 font-medium text-slate-800">{r.company}<div className="text-xs font-normal text-slate-500">{r.email}</div></td>
                <td className="px-5 py-3 text-slate-700">{r.doc_type}</td>
                <td className="px-5 py-3 font-mono text-xs text-slate-600">{r.expiry || "—"}</td>
                <td className="px-5 py-3"><span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${r.st.tone}`}>{r.st.label}</span></td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-3">
                    {(r.st.key === "expiring" || r.st.key === "expired") && (
                      <button onClick={() => sendReminder(r)} disabled={sending === `${r.sid}-${r.doc_type}`} className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-amber-800 disabled:opacity-50" data-testid={`compliance-remind-${i}`}>
                        {sending === `${r.sid}-${r.doc_type}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BellRing className="h-3.5 w-3.5" />} Send reminder
                      </button>
                    )}
                    <a href={`${api.defaults.baseURL}/files/${r.file_id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid={`compliance-view-${i}`}><Download className="h-3.5 w-3.5" /> View</a>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
